import { SECTORS } from '../constants/sectors';
import { STATE_DATA } from '../constants/stateData';
import { fmt } from './formatters';

// Round to nearest $100 — keeps all estimates defensible
const r100 = v => Math.round(v / 100) * 100;

export function calc(d) {
  const fleet   = parseInt(d.fleetSize)    || 0;
  const states  = parseInt(d.statesOp)     || 1;
  const subs    = parseInt(d.subcontractors)|| 0;
  const emp     = parseInt(d.employees)    || 5;
  const rev     = parseInt(d.revenue)      || 500000;
  const claims  = parseInt(d.claims)       || 0;
  const propVal = parseInt(d.propertyValue)|| 200000;
  const yrs     = parseInt(d.yearsInBusiness)|| 5;
  const payroll = parseInt(d.annualPayroll) || r100(rev * 0.35);
  const drivers = parseInt(d.drivers)      || fleet;
  const units   = parseInt(d.units)        || 0;
  // Experience modification factor — applied to WC base premium
  const expMod  = Math.max(0.5, Math.min(2.5, parseFloat(d.experienceMod) || 1.0));

  const sector    = SECTORS.find(s => s.value === d.industry)?.sector || "construction";
  const stateInfo = STATE_DATA[d.primaryState] || { wcMod:1, glMod:1, autoMod:1, propMod:1 };

  // Size-adjusted benchmark multiplier (used for industry benchmark display scaling)
  const benchmarkAdj = Math.min(3.5, Math.max(0.35,
    0.40 * Math.pow(Math.max(rev, 50000) / 100000, 0.45)
  ));

  let avgSavingsForSize = 0;

  // Risk score — whole number, reflects all risk factors
  const riskScore = Math.round(Math.min(96, Math.max(8,
    claims * 11 + Math.min(states * 4, 18) + Math.min(subs * 1.8, 20) + Math.min(fleet * 0.8, 14) +
    (yrs < 3 ? 18 : yrs < 6 ? 8 : 0) + (d.hasLosses === "yes" ? 14 : 0) +
    (stateInfo.litigious ? 8 : 0) + (stateInfo.scaffoldLaw ? 12 : 0) +
    (stateInfo.wildfire ? 5 : 0) + (stateInfo.windHail ? 6 : 0) + 12
  )));

  // ── DYNAMIC SAVINGS RATES ─────────────────────────────────────────────────
  // Savings percentages scale with the prospect's specific risk leverage factors.
  // Higher savings when claims history, multi-state, or large sub exposure exist —
  // those are highest-leverage re-marketing and compliance opportunities.
  const hasClaims  = claims > 0;
  const highClaims = claims > 2;
  const multiState = states > 1;
  const hasSubs    = subs > 0;
  const largeSubs  = subs > 5;
  const highExpMod = expMod > 1.1;

  // Auto: 15–22% (fleet remarketing + driver classification)
  const autoSavePct = Math.min(0.22, 0.15
    + (hasClaims  ? 0.03 : 0)
    + (multiState ? 0.02 : 0)
    + (highClaims ? 0.02 : 0));

  // GL: 18–26% (sub cert compliance + class code correction)
  const glSavePct = Math.min(0.26, 0.18
    + (hasSubs    ? 0.04 : 0)
    + (hasClaims  ? 0.02 : 0)
    + (largeSubs  ? 0.02 : 0));

  // WC: 15–28% (experience mod management + payroll audit)
  const wcSavePct = Math.min(0.28, 0.15
    + (hasClaims  ? 0.06 : 0)
    + (multiState ? 0.04 : 0)
    + (highClaims ? 0.03 : 0)
    + (highExpMod ? 0.02 : 0));

  // Property: 10–18% (blanket vs scheduled + deductible restructuring)
  const propSavePct = Math.min(0.18, 0.10
    + (states > 2 ? 0.03 : 0)
    + (hasClaims  ? 0.02 : 0)
    + ((stateInfo.windHail || stateInfo.flood) ? 0.03 : 0));

  // Umbrella: 16–22% (underlying limit restructuring)
  const umbSavePct = Math.min(0.22, 0.16
    + (multiState ? 0.03 : 0)
    + (hasClaims  ? 0.02 : 0)
    + (hasSubs    ? 0.01 : 0));

  // Inland marine: 12–16% (scheduled vs blanket, equipment audits)
  const inlandSavePct = Math.min(0.16, 0.12 + (hasClaims ? 0.02 : 0) + (states > 2 ? 0.02 : 0));

  let cur, opt, gaps = [], userEntered = {}, wcEst = 0;

  if (sector === "realestate") {
    const ctRE = d.coverageTypes || [];

    // Property: per-unit rate × state modifier × catastrophe loads
    const propPerUnit = r100(
      (d.industry === "re_commercial" ? 2200 : d.industry === "re_hoa" ? 900 : 1500)
      * stateInfo.propMod
      * (stateInfo.windHail ? 1.35 : 1)
      * (stateInfo.flood    ? 1.18 : 1)
    );
    const rePropEst  = units > 0 ? r100(propPerUnit * units) : r100(propVal * 0.006 * stateInfo.propMod);
    // GL: $1,200 base + per-unit exposure + claims loading (GL rate per unit declines with scale)
    const reGlEst    = r100((1800 + units * 100 + claims * 500) * stateInfo.glMod);
    // Umbrella: ~14% of GL base
    const reUmbEst   = r100(Math.max(reGlEst * 0.14, 1200) * stateInfo.glMod);
    const reAutoEst  = r100((1900 + fleet * 2400) * stateInfo.autoMod);
    const reEoEst    = r100(2800 + rev * 0.0007);
    const reFloodEst = r100(units * 380 + propVal * 0.0025);

    const entProp  = d.currentProp      && parseInt(d.currentProp);
    const entGL    = d.currentGL        && parseInt(d.currentGL);
    const entUmb   = d.currentUmbrella  && parseInt(d.currentUmbrella);
    const entAuto  = d.currentAuto      && parseInt(d.currentAuto);
    const entEO    = d.currentEO        && parseInt(d.currentEO);
    const entFlood = d.currentFlood     && parseInt(d.currentFlood);

    cur = {
      property: ctRE.includes("property") ? (entProp  || rePropEst)  : 0,
      gl:       ctRE.includes("gl")       ? (entGL    || reGlEst)    : 0,
      umbrella: ctRE.includes("umbrella") ? (entUmb   || reUmbEst)   : 0,
      auto:     ctRE.includes("auto")     ? (entAuto  || reAutoEst)  : 0,
      eo:       ctRE.includes("eo")       ? (entEO    || reEoEst)    : 0,
      flood:    ctRE.includes("flood")    ? (entFlood || reFloodEst) : 0,
    };
    userEntered = {
      property: !!entProp, gl: !!entGL, umbrella: !!entUmb,
      auto: !!entAuto, eo: !!entEO, flood: !!entFlood,
    };
    opt = {
      property: r100(cur.property * (1 - propSavePct)),
      gl:       r100(cur.gl       * (1 - glSavePct)),
      umbrella: r100(cur.umbrella * (1 - umbSavePct)),
      auto:     r100(cur.auto     * (1 - autoSavePct)),
      eo:       r100(cur.eo       * 0.86),
      flood:    r100(cur.flood    * 0.92),
    };

    if (!d.coverageTypes.includes("gl")) gaps.push({level:"red",exposureCost:13000,title:"No Premises Liability — Slip/Fall Uninsured",desc:`${units > 0 ? units + " units" : "Your property"} with no GL creates uncapped personal liability for every tenant injury and visitor slip/fall. Avg third-party injury claim: $65,000.`});
    if (stateInfo.flood && !d.coverageTypes.includes("flood")) gaps.push({level:"red",exposureCost:19000,title:"Flood Not Covered — High Exposure State",desc:`${stateInfo.name} flood exposure is excluded from standard property policies. A single storm event can total a property. Avg commercial property loss: $95,000.`});
    if (stateInfo.windHail && !d.coverageTypes.includes("windstorm")) gaps.push({level:"amber",title:"Windstorm / Hurricane Endorsement Missing",desc:`${stateInfo.name} requires a separate windstorm endorsement — named storm exclusions are standard in this market. Your property policy may have a complete wind exclusion.`});
    if (stateInfo.earthquakeRisk && !d.coverageTypes.includes("earthquake")) gaps.push({level:"amber",title:"Earthquake Not Covered",desc:`Standard property in ${stateInfo.name} excludes earthquake. A separate earthquake policy or CEQA endorsement is required to close this gap.`});
    if (["re_broker","re_pm"].includes(d.industry) && !d.coverageTypes.includes("eo")) gaps.push({level:"red",exposureCost:13000,title:"No E&O — Professional Liability Uninsured",desc:"Property managers and brokers face professional liability claims from lease disputes, management errors, and disclosure failures — without E&O, defense and judgment costs are personal."});
    if (units > 4 && !d.coverageTypes.includes("renters_liability")) gaps.push({level:"amber",title:"Fair Housing / Discrimination Liability Gap",desc:`${units} units with no fair housing coverage. HUD complaints average $50K+ to defend regardless of outcome and are excluded from standard property policies.`});
    if (stateInfo.sinkhole && !d.coverageTypes.includes("sinkhole")) gaps.push({level:"amber",title:"Sinkhole Coverage Missing — Florida Requirement",desc:"Florida sinkhole activity is significant and commercial properties are not automatically covered without endorsement. This is a standard omission in non-specialist policies."});
    if ((rev >= 2000000 || emp >= 20) && !d.coverageTypes.includes("umbrella")) gaps.push({level:"amber",exposureCost:36000,title:"No Umbrella — Portfolio Aggregate Exposure",desc:`A portfolio of ${units > 0 ? units + " units" : "multiple properties"} carries significant aggregate liability. Umbrella coverage is standard at this portfolio size. Avg excess judgment: $180,000.`});

  } else {
    // ── WC — NCCI/ISO calibrated rates as fraction of payroll ───────────────
    // Rates reflect base manual rates in standard (non-monopolistic) states.
    // State wcMod multiplier applied on top. Experience mod applied separately.
    // Sources: NCCI FL base rates; class codes 5551 (roofing), 5213 (demo), etc.
    const wcBaseRates = {
      roofing:0.105, demolition:0.130, framing:0.090, concrete:0.075, paving:0.065,
      gc:0.055, restoration:0.055, fabrication:0.048, painting:0.048, landscaping:0.045,
      solar:0.045, plumbing:0.040, hvac:0.040, electrical:0.038, other:0.055,
    };
    const wcBaseRate = (wcBaseRates[d.industry] || 0.055) * stateInfo.wcMod;
    // Apply experience mod — the single largest WC pricing lever
    wcEst = r100(payroll * wcBaseRate * expMod);

    // ── Commercial Auto — per-vehicle rates by trade ────────────────────────
    // Range: $1,800–$4,200/vehicle. Roofing/demo highest; landscaping/HVAC lowest.
    const autoPerVeh = {
      demolition:4200, roofing:3400, paving:3800, concrete:3600, framing:3200,
      gc:2800, restoration:2600, fabrication:2400, landscaping:1900,
      painting:2200, solar:2100, plumbing:2200, hvac:2000, electrical:2000, other:2600,
    }[d.industry] || 2600;
    const autoEst = r100((autoPerVeh * Math.max(fleet, drivers) + states * 200 + claims * 400) * stateInfo.autoMod);

    // ── GL — revenue-based rates per $1,000 revenue ─────────────────────────
    // Revenue basis more accurate than payroll for GL — aligns with ISO CGL rating.
    // Range: $4,000–$18,000 per $1M revenue depending on trade.
    const glRatesPerRevK = {
      demolition:15, roofing:14, framing:10, concrete:9, paving:8, gc:8,
      painting:7, restoration:8, fabrication:6, solar:6, plumbing:5.5,
      hvac:5, electrical:5, landscaping:4, other:7,
    };
    const glBaseRate = (glRatesPerRevK[d.industry] || 7) * stateInfo.glMod * (stateInfo.scaffoldLaw ? 1.45 : 1);
    const glEst = r100((rev / 1000) * glBaseRate + subs * 200 + claims * 650);

    // ── Property — 0.4%–1.2% of insured value ───────────────────────────────
    const propEst = r100((propVal * 0.006 + states * 200) * stateInfo.propMod);

    // ── Umbrella — 8%–15% of underlying GL premium ──────────────────────────
    const umbEst = r100(Math.max(glEst * 0.12, 1400) * stateInfo.glMod + states * 200);

    // ── Inland Marine — 1.5%–2.5% of equipment value ────────────────────────
    const inlEst = propVal > 30000 ? r100(propVal * 0.02) : 0;

    const ct = d.coverageTypes || [];
    const entAuto = d.currentAuto      && parseInt(d.currentAuto);
    const entGL   = d.currentGL        && parseInt(d.currentGL);
    const entWC   = d.currentWC        && parseInt(d.currentWC);
    const entProp = d.currentProp      && parseInt(d.currentProp);
    const entUmb  = d.currentUmbrella  && parseInt(d.currentUmbrella);
    const entInl  = d.currentInland    && parseInt(d.currentInland);

    cur = {
      auto:     ct.includes("auto")     ? (entAuto || autoEst) : 0,
      gl:       ct.includes("gl")       ? (entGL   || glEst)   : 0,
      wc:       ct.includes("wc")       ? (entWC   || wcEst)   : 0,
      prop:     ct.includes("property") ? (entProp || propEst) : 0,
      umbrella: ct.includes("umbrella") ? (entUmb  || umbEst)  : 0,
      inland:   ct.includes("inland")   ? (entInl  || inlEst)  : 0,
    };
    userEntered = {
      auto: !!entAuto, gl: !!entGL, wc: !!entWC,
      prop: !!entProp, umbrella: !!entUmb, inland: !!entInl,
    };
    opt = {
      auto:     r100(cur.auto     * (1 - autoSavePct)),
      gl:       r100(cur.gl       * (1 - glSavePct)),
      wc:       r100(cur.wc       * (1 - wcSavePct)),
      prop:     r100(cur.prop     * (1 - propSavePct)),
      umbrella: r100(cur.umbrella * (1 - umbSavePct)),
      inland:   r100(cur.inland   * (1 - inlandSavePct)),
    };

    // GL — always required for construction (contractually on job sites and by GCs)
    if (!ct.includes("gl")) gaps.push({level:"red",exposureCost:13000,title:"No General Liability — Contractual Requirement",desc:"GL is contractually required on most job sites and by most GCs — operating without it puts every contract at risk and creates direct personal liability for all third-party injuries and property damage. Avg claim: $65,000."});
    // WC — only if employees exist
    if (emp > 0 && !ct.includes("wc")) gaps.push({level:"red",exposureCost:8400,title:"Workers Compensation Missing — Statutory Violation",desc:`You have ${emp} employee${emp!==1?"s":""} but no WC selected — this is a statutory requirement in most states carrying fines of $1,000+/day plus full personal liability for any lost-time injury. Avg claim: $42,000.`});
    // Auto — only if they have vehicles or drivers
    const veh=Math.max(fleet,drivers);
    if (veh > 0 && !ct.includes("auto")) gaps.push({level:"red",exposureCost:7600,title:`No Commercial Auto — ${veh} Vehicle${veh!==1?"s":""} Exposed`,desc:`You have ${veh} vehicle${veh!==1?"s":""} but no commercial auto selected — personal auto policies explicitly exclude business use. Every mile driven is uncovered liability. Avg claim: $38,000.`});
    // Umbrella — only if revenue > $1M
    if (rev > 1000000 && !ct.includes("umbrella")) {
      const uLevel=(rev>=10000000||emp>=75)?"red":"amber";
      gaps.push({level:uLevel,exposureCost:36000,title:"No Umbrella / Excess Liability",desc:`At your revenue level, a single excess judgment could exceed your GL limits — umbrella provides critical protection. Avg excess judgment: $180,000.`});
    }
    // NY Scaffold Law — warn even if they have GL (limits usually insufficient)
    if (stateInfo.scaffoldLaw) gaps.push({level:"amber",title:"NY Scaffold Law — GL Limits Likely Insufficient",desc:"Labor Law 240/241 imposes absolute liability for gravity-related injuries regardless of worker fault. A single scaffold claim routinely exhausts standard $1M GL limits. Umbrella coverage is essential on every NY project."});
    // Inland — construction with any meaningful equipment
    if (!ct.includes("inland") && propVal > 30000) gaps.push({level:"amber",exposureCost:5600,title:"Tools & Equipment Uninsured in Transit",desc:`Construction businesses with equipment on job sites face significant theft and damage exposure without inland marine. Standard GL and property don't cover tools in transit or on job sites. Avg theft/damage claim: $28,000.`});
    // Property — only if they appear to own a building (high property value)
    if (!ct.includes("property") && propVal > 300000) gaps.push({level:"amber",exposureCost:19000,title:"Commercial Property Coverage Missing",desc:`With ${fmt(propVal)} in property value, a fire or catastrophic event without coverage creates a significant uninsured loss. Standard GL does not cover your own building or equipment. Avg loss: $95,000.`});
    // Claims history
    if (claims > 2) gaps.push({level:"amber",title:"Claim History Driving Up Experience Mod",desc:`${claims} claims in 3 years is inflating your experience mod and adding excess WC premiums. Proactive loss control and claims advocacy can reverse this trend over 2–3 policy years.`});
    // Wildfire state
    if (stateInfo.wildfire) gaps.push({level:"amber",title:"Wildfire Exclusion Risk in Policy Language",desc:`${stateInfo.name} carriers are actively adding wildfire exclusions to standard property and builders risk policies at renewal. Verify your current policy form explicitly covers wildfire.`});
    // SDI at scale
    if (emp > 20 && subs === 0) gaps.push({level:"amber",title:"No Subcontractor Default Protection",desc:`With ${emp} employees and your revenue profile, SDI coverage protects against subcontractor non-performance — increasingly standard at your revenue scale.`});
    // Blue — has coverage but overpaying / optimization opportunity
    if (rev < 500000 && emp < 5 && yrs < 3) gaps.push({level:"blue",title:"New Business Surcharge — Reducible",desc:"Carriers automatically surcharge businesses under 3 years at 15–35% above standard market rates. AOR accesses specialty new-venture markets that don't apply this loading."});
    if (ct.includes("wc") && expMod > 1.1) gaps.push({level:"blue",title:"WC Premium Elevated by Experience Mod",desc:`Your mod of ${expMod.toFixed(2)} is adding an estimated ${fmt(r100(wcEst*(1-1/expMod)))} above the 1.0 market baseline annually. AOR's loss control, claims advocacy, and payroll audit defense programs target measurable mod reductions within 2–3 policy years.`});
    if (ct.includes("gl") && subs > 3) gaps.push({level:"blue",title:"GL Certificate Compliance Gap — Audit Surcharge Risk",desc:`${subs} subcontractors without verified certificates on file creates GL audit surcharge exposure at renewal — typically 10–20% added to your final premium. AOR's cert tracking program systematically eliminates this risk.`});
    if (ct.includes("auto") && fleet > 4) gaps.push({level:"blue",title:"Fleet Size Qualifies for Rating Discounts",desc:`At ${fleet} vehicles, you likely qualify for fleet discount programs and driver classification corrections that standard markets miss. AOR fleet specialists average 12–18% savings on accounts this size.`});
  }

  // Peer average savings: sum per-line savings at published peer rates (coverage-aware)
  const peerRates = {
    auto:0.20, gl:0.21, wc:0.20, prop:0.14, umbrella:0.18, inland:0.13,
    property:0.18, eo:0.14, flood:0.08,
  };
  const aorTotalSavings = r100(
    Object.keys(cur).reduce((sum, key) => sum + ((cur[key] || 0) - (opt[key] || 0)), 0)
  );
  // Peer average must always sit below AOR savings (AOR outperforms the market average).
  // Cap at 85% of AOR savings so the relationship Current > Peer > AOR Optimized always holds.
  avgSavingsForSize = r100(Math.min(
    Object.keys(cur).reduce((sum, key) => sum + (cur[key] || 0) * (peerRates[key] || 0.18), 0),
    aorTotalSavings * 0.85
  ));

  return {
    cur, opt, riskScore, gaps, rev, emp, fleet, sector, stateInfo, primaryState: d.primaryState,
    avgSavingsForSize, benchmarkAdj, payroll, expMod, states, userEntered,
    autoSavePct, glSavePct, wcSavePct, propSavePct, umbSavePct, inlandSavePct,
    expModEntered: !!d.experienceMod,
    wcEst,
    exposureTotal: gaps.reduce((sum, g) => sum + (g.exposureCost || 0), 0),
  };
}



// ─── UI COMPONENTS ───────────────────────────────────────────────────────────